import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, Star, ShieldCheck, Users } from "lucide-react";
import { BIZ, SERVICES, ABOUT, REPUTATION } from "./judyCabreraData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import AccountingInquiryForm from "@/components/AccountingInquiryForm";

const CHARCOAL = "#2D2A26";
const RED = "#DC2626";
const CREAM = "#FAF6F0";
const SLATE = "#5C564C";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function JudyCabreraSite() {
  const [open, setOpen] = useState(false);
  const links = [["Services", "#services"], ["Meet Judy", "#about"], ["For Business Owners", "#business"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: CHARCOAL, fontFamily: "'Inter', sans-serif" }} data-testid="judycabrera-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <div data-testid="accounting-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: RED }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur shadow-sm">
        <nav className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="judycabrera-logo" className="flex items-center gap-2.5 font-extrabold text-base" style={{ color: CHARCOAL }}>
            <img src="/accounting/judy-cabrera-accounting-concept/logo-mark.jpg" alt="" className="h-9 w-auto object-contain" />
            JUDY M. CABRERA <span style={{ color: RED }}>ACCOUNTING</span>
          </a>
          <div className="hidden md:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium hover:opacity-70" style={{ color: SLATE }}>{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="judycabrera-header-cta" className="hidden sm:inline-flex rounded-full px-5 py-2 text-sm font-semibold text-white" style={{ background: RED }}>Call Now</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="judycabrera-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium" style={{ color: CHARCOAL }}>{l}</a>)}</div>}
      </header>

      {/* HERO — real community-seminar photo */}
      <section className="relative min-h-[62vh] flex items-end" data-testid="judycabrera-hero">
        <img src="/accounting/judy-cabrera-accounting-concept/community-seminar.jpg" alt="Judy M. Cabrera Accounting Services conducting a business-registration information session in Naic, Cavite" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(45,42,38,0.15) 0%, rgba(45,42,38,0.6) 55%, rgba(45,42,38,0.95) 100%)" }} />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 w-full pb-12">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#F5A3A3" }}>Naic, Cavite</span>
            <h1 className="mt-4 text-3xl sm:text-5xl font-extrabold text-white leading-[1.15]">{BIZ.tagline}</h1>
            <p className="mt-4 text-white/75 max-w-md text-sm">Practical, end-to-end accounting support built around a named CPA — {BIZ.principal}.</p>
            <div className="mt-7 flex flex-wrap gap-4">
              <a href="#contact" data-testid="judycabrera-hero-cta" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white" style={{ background: RED }}>Ask About Your Books</a>
              <a href={`tel:${BIZ.phone}`} data-testid="judycabrera-hero-call" className="rounded-full px-7 py-3.5 text-sm font-semibold text-white border border-white/30">Call {BIZ.phoneDisplay}</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES — horizontal scrolling strip of badges, not a static grid */}
      <section id="services" className="py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ color: CHARCOAL }}>Services</motion.h2>
        </div>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 overflow-x-auto">
          <div className="flex gap-4 pb-3 w-max">
            {SERVICES.map((s) => (
              <motion.div key={s.title} {...fadeUp} className="w-56 shrink-0 p-5 bg-white rounded-lg shadow-sm border-l-4" style={{ borderColor: RED }}>
                <h3 className="font-semibold text-sm" style={{ color: CHARCOAL }}>{s.title}</h3>
                <p className="mt-1.5 text-xs" style={{ color: SLATE }}>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET JUDY + FOR BUSINESS OWNERS — combined two-column, avatar crop + bio */}
      <section id="about" className="py-16" style={{ background: "#F0E6DC" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="mx-auto md:mx-0 rounded-full overflow-hidden shadow-md shrink-0" style={{ width: 120, height: 120 }}>
            <img src="/accounting/judy-cabrera-accounting-concept/community-seminar.jpg" alt="Judy M. Cabrera at a community accounting session" className="w-full h-full object-cover" style={{ objectPosition: "50% 30%" }} />
          </div>
          <div>
            <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: CHARCOAL }}>Meet Judy</motion.h2>
            <motion.p {...fadeUp} className="text-sm mb-4 font-semibold" style={{ color: RED }}>{BIZ.principal}</motion.p>
            <motion.p {...fadeUp} className="text-sm leading-relaxed" style={{ color: SLATE }}>{ABOUT.background}</motion.p>
            <motion.div {...fadeUp} className="mt-4 flex items-start gap-2.5 rounded-lg p-4 text-xs bg-white">
              <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: RED }} />
              {ABOUT.credential}
            </motion.div>
            <motion.div {...fadeUp} id="business" className="mt-4 flex items-start gap-2.5 text-sm">
              <Users size={18} style={{ color: RED }} className="shrink-0 mt-0.5" />
              <p style={{ color: SLATE }}>{ABOUT.community}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BRAND — Judy's own "JMC" personal-brand photo and tagline */}
      <section className="py-14 bg-white">
        <div className="max-w-md mx-auto px-5 sm:px-8 text-center">
          <img src="/accounting/judy-cabrera-accounting-concept/jmc-brand.jpg" alt="Judy M. Cabrera Accounting Services — Rooted in Values, Driven by Results" className="w-full h-auto object-contain" />
        </div>
      </section>

      {/* COMMUNITY IMPACT — additional real photos from Judy's seminar work */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-bold text-center mb-10" style={{ color: CHARCOAL }}>Community Impact</motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="rounded-lg overflow-hidden shadow-sm">
              <img src="/accounting/judy-cabrera-accounting-concept/entrepreneur-seminar.jpg" alt="Judy M. Cabrera speaking at a local entrepreneurship seminar" className="w-full h-56 object-cover" />
            </motion.div>
            <motion.div {...fadeUp} className="rounded-lg overflow-hidden shadow-sm">
              <img src="/accounting/judy-cabrera-accounting-concept/registration-seminar-group.jpg" alt="Judy M. Cabrera Accounting Services team at a business-registration information session" className="w-full h-56 object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* REPUTATION */}
      <section className="py-12" style={{ background: CHARCOAL }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={RED} style={{ color: RED }} />)}
          </div>
          <p className="text-lg font-bold text-white">{REPUTATION.rating}★ local-directory rating</p>
          <p className="mt-1.5 text-xs text-white/60">{REPUTATION.note}</p>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-16" style={{ background: "#F0E6DC" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl font-bold mb-6" style={{ color: CHARCOAL }}>Office</h2>
            <ul className="space-y-4 text-sm mb-6">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: RED }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: RED }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="judycabrera-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a> / <a href={`tel:${BIZ.mobile}`} className="hover:underline">{BIZ.mobileDisplay}</a></li>
              <li className="flex items-start gap-3"><Facebook size={18} style={{ color: RED }} className="mt-0.5 shrink-0" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="judycabrera-facebook-link" className="hover:underline">Facebook page</a></li>
            </ul>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[220px]">
              <iframe title="Judy M. Cabrera Accounting Services map" src={BIZ.mapEmbed} className="w-full h-full min-h-[220px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <AccountingInquiryForm
              testidPrefix="judycabrera"
              businessName={BIZ.name}
              accent={RED}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
              messengerUrl={BIZ.facebook}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs" style={{ color: SLATE }}>
          <img src="/accounting/judy-cabrera-accounting-concept/logo-mark.jpg" alt="Judy M. Cabrera Accounting Services" className="h-8 w-auto object-contain" />
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="judy-cabrera-accounting-concept" />
    </div>
  );
}
